import { EXPERIMENT_HEX, MonitoringSignals } from "./SiteTile";
import {
  EXPERIMENT_STATUS,
  PLOTMAP_STATUS,
  requiresClientAction,
} from "../../constants";
import React, { useMemo, useState } from "react";

import { CountryFlag } from "../../components/CountryFlag/CountryFlag";
import PropTypes from "prop-types";
import { iso31661 } from "iso-3166";

const countryLabel = (alpha) =>
  iso31661.find((c) => c.alpha3 === alpha)?.name ?? "";

// Coloured status pill mirroring the site tile's dot + label.
const StatusCell = ({ color, label }) => (
  <span className="cf-table__status" style={{ color }}>
    <span className="cf-table__dot" style={{ background: color }} />
    {label}
  </span>
);

StatusCell.propTypes = {
  color: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

// Row-based alternative to the bento grid — same data, denser layout, with
// click-to-sort column headers.
export const SiteTable = ({
  sites,
  showExperiment,
  trials,
  monitoring,
  canEdit,
  onOpen,
  onEdit,
  onViewResults,
}) => {
  const stop = (event) => event.stopPropagation();
  // null → keep the order the parent already sorted in (via the Sort select).
  const [sortBy, setSortBy] = useState(null);

  const rows = useMemo(() => {
    if (!sortBy) return sites;
    const comparators = {
      name: (a, b) => a.display_name.localeCompare(b.display_name),
      crop: (a, b) => (a.crop || "").localeCompare(b.crop || ""),
      country: (a, b) =>
        countryLabel(a.country).localeCompare(countryLabel(b.country)),
      experiment: (a, b) =>
        (a.experiment_status ?? -1) - (b.experiment_status ?? -1),
      plotmap: (a, b) => (a.plotmap_status ?? 0) - (b.plotmap_status ?? 0),
    };
    const sorted = [...sites].sort(comparators[sortBy.key]);
    return sortBy.dir === "desc" ? sorted.reverse() : sorted;
  }, [sites, sortBy]);

  const toggleSort = (key) =>
    setSortBy((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );

  // Sortable <th>: label + a direction caret (muted when the column is inactive).
  const Th = ({ label, sortKey }) => {
    const active = sortBy?.key === sortKey;
    return (
      <th
        className="cf-table__th cf-table__th--sort"
        aria-sort={
          active ? (sortBy.dir === "asc" ? "ascending" : "descending") : "none"
        }
        onClick={() => toggleSort(sortKey)}
      >
        {label}
        <i
          className={
            "cf-table__sort fa-solid " +
            (active
              ? sortBy.dir === "asc"
                ? "fa-sort-up is-active"
                : "fa-sort-down is-active"
              : "fa-sort")
          }
          aria-hidden="true"
        />
      </th>
    );
  };

  Th.propTypes = {
    label: PropTypes.string.isRequired,
    sortKey: PropTypes.string.isRequired,
  };

  return (
    <div className="cf-table-wrap">
      <table className="cf-table">
        <thead>
          <tr>
            <Th label="Site" sortKey="name" />
            <Th label="Crop" sortKey="crop" />
            <Th label="Country" sortKey="country" />
            {showExperiment && <Th label="Field map" sortKey="experiment" />}
            <Th label="Plot map" sortKey="plotmap" />
            <th className="cf-table__th">Activity</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((site) => {
            const experiment =
              EXPERIMENT_STATUS.properties[site.experiment_status];
            const plotmap =
              PLOTMAP_STATUS.properties[
                site.plotmap_status ?? PLOTMAP_STATUS.NOT_DELIVERED
              ];
            const hasResults = trials.some((trial) => trial.id === site.id);

            return (
              <tr
                key={site.id}
                className={
                  "cf-table__row" +
                  (requiresClientAction(site) ? " cf-table__row--action" : "")
                }
                onClick={() => onOpen(site)}
              >
                <td className="cf-table__name">{site.display_name}</td>
                <td className={site.crop ? "" : "cf-table__muted"}>
                  {site.crop || "No crop"}
                </td>
                <td className={site.country ? "" : "cf-table__muted"}>
                  {site.country ? (
                    <span className="cf-table__country">
                      <CountryFlag alpha={site.country} size="sm" />
                      {countryLabel(site.country) || "Unknown"}
                    </span>
                  ) : (
                    "No country"
                  )}
                </td>
                {showExperiment && (
                  <td>
                    {experiment ? (
                      <StatusCell
                        color={EXPERIMENT_HEX[experiment.color] ?? "#9aa0a6"}
                        label={experiment.label}
                      />
                    ) : (
                      "—"
                    )}
                  </td>
                )}
                <td>
                  {plotmap ? (
                    <StatusCell color={plotmap.color} label={plotmap.label} />
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  <MonitoringSignals monitoring={monitoring?.[site.id]} />
                </td>
                <td className="cf-table__actions" onClick={stop}>
                  {canEdit && (
                    <button
                      type="button"
                      className="cf-iconbtn"
                      data-tooltip-id="tooltip"
                      data-tooltip-content="Edit site"
                      onClick={() => onEdit(site)}
                    >
                      <i className="fa-solid fa-pencil" />
                    </button>
                  )}
                  {hasResults && (
                    <button
                      type="button"
                      className="btn-icon btn-icon--solid cf-resultbtn"
                      data-tooltip-id="tooltip"
                      data-tooltip-content="View result on map"
                      onClick={() => onViewResults(site)}
                    >
                      <i className="fa-solid fa-earth-europe" />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

SiteTable.propTypes = {
  sites: PropTypes.array.isRequired,
  showExperiment: PropTypes.bool,
  trials: PropTypes.array,
  monitoring: PropTypes.object,
  canEdit: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onViewResults: PropTypes.func.isRequired,
};
